<template>
	<div class="create-group-wrapper">
		<StandardModal
			v-if="!showAddMembers"
			open
			class="announcement-composer-modal"
			:title="$t('chat_drawer_addbubble_create')"
			:confirmText="$t('common_button_next')"
			@confirm="onNext"
			@cancel="onClose"
			ref="modal"
			:confirmProps="{ disabled }"
			:tabOrder="[
				{ css: '.selected-container.clickable', clickOnEnter: true },
				{ css: '.scheduled-date-container', clickOnEnter: true },
				{ css: '.ql-editor' },
				{ css: '.attachment-btn', clickOnEnter: true },
			]"
		>
			<div class="create-group-content">
				<div class="group-icon-picker-container">
					<GroupIconPicker large @change="onGroupIconChange" />
				</div>
				<FormItem :label="$t('groupsettings_option_name')">
					<Input v-model="groupTitle" autoFocus />
				</FormItem>
				<FormItem :label="$t('groupsettings_option_category')">
					<Dropdown
						:options="categoryOptions"
						v-model="categorySelected"
						autoFocus
						:placeholder="$t('groupsettings_option_category_uncatagorized')"
					>
						<template v-slot="{ option }">
							<Uncategorized v-if="option && option.id === NO_CATEGORY" />
							<div
								v-if="option && option.id !== NO_CATEGORY"
								v-text="option.label"
							/>
						</template>
						<template #selected="{ option }">
							<Uncategorized v-if="option && option.id === NO_CATEGORY" />
							<div
								v-if="option && option.id !== NO_CATEGORY"
								v-text="option.label"
							/>
						</template>
					</Dropdown>
				</FormItem>
			</div>
		</StandardModal>

		<AddMembersModal
			v-if="showAddMembers"
			@add="addUsersAndCreateGroup"
			@cancel="onBackToGroupDetails"
			@close="onCloseAddMembers"
			:confirmText="'Create Group'"
			:loading="isCreatingGroup"
			:initialUsers="props.initialUsers"
			:maxLimit="maxLimit"
			:large="false"
			:cancelText="$t('common_button_back')"
		>
			<template #footer-unselected>
				<Button
					class="skip-button"
					primary
					link
					@click="onSkipForNow"
					:loading="isSkippingForNow"
					>{{ $t('common_button_skip_now') }}</Button
				>
			</template>
		</AddMembersModal>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue'
import {
	getUser,
	setUser,
	getCategories,
	getMyUser,
	sortUsersByFullname,
	getOrganization,
	getMyUserId,
} from '@/store'
import {
	Input,
	Dropdown,
	FormItem,
	StandardModal,
	Button,
	useAlert,
} from '@/components/common'
import Uncategorized from '@/components/OrgManagement/common/Uncategorized'
import GroupIconPicker from '@/components/common/GroupIconPicker'
import { createChat, addUsersToChat } from '@/api/chat'
import AddMembersModal from './AddMembersModal'
import { sortCategoriesByDisplayName } from '@/store/chats'
import { useRouter } from 'vue-router'

const NO_CATEGORY = 'none'

const props = defineProps({ initialUsers: Array })
const emit = defineEmits(['close'])
const router = useRouter()
const alert = useAlert()

const org = getOrganization()
const categoryOptions = getCategories()
	.sort(sortCategoriesByDisplayName)
	.map((item) => ({ id: item.id, label: item.displayname }))
categoryOptions.unshift({ id: NO_CATEGORY })

const groupTitle = ref('')
const groupIconData = ref({})
const users = ref([getMyUser()])
const categorySelected = ref(NO_CATEGORY)
const showAddMembers = ref(false)
const isCreatingGroup = ref(false)
const isSkippingForNow = ref(false)
const maxLimit = ref(org.bubble_membership_cap)

const disabled = computed(() => {
	return !groupTitle.value || isCreatingGroup.value
})

function onClose() {
	emit('close')
}

function onCloseAddMembers() {
	if (isCreatingGroup.value) return
	showAddMembers.value = false
	onClose()
}

function onBackToGroupDetails() {
	if (isCreatingGroup.value) return
	showAddMembers.value = false
}

async function addUsersAndCreateGroup(newUsers) {
	isCreatingGroup.value = true
	newUsers.forEach(setUser)
	const me = getMyUser()
	const allIds = users.value.concat(newUsers).map((u) => u.id)
	const uniqueIds = Array.from(new Set(allIds))
	users.value = uniqueIds
		.map(getUser)
		.filter((u) => u)
		.map((u) => ({ ...u, canRemove: u.id !== me.id }))
		.sort(sortUsersByFullname)
	await createGroup()
}

function onNext() {
	showAddMembers.value = true
}

function onSkipForNow() {
	isSkippingForNow.value = true
	createGroup()
}

async function createGroup() {
	if (!groupTitle.value) return

	const body = { title: groupTitle.value }
	if (categorySelected.value && categorySelected.value !== NO_CATEGORY) {
		body.category_id = categorySelected.value
	}
	if (Object.keys(groupIconData.value).length) {
		Object.assign(body, groupIconData.value)
	}

	try {
		// create the group, current user is added automatically
		const chat = await createChat(body)
		// add remaining users to the group
		await addOtherUsersToGroup(chat.id)
		onClose()
		router.push('/recents/' + chat.id)
	} catch (e) {
		console.error(e)
		alert.error(e)
		isCreatingGroup.value = false
	}
}

function addOtherUsersToGroup(chatId) {
	const myId = getMyUserId()
	const userIds = users.value.map((u) => u.id).filter((id) => id !== myId)
	if (!userIds.length) return
	return addUsersToChat(chatId, userIds)
}

function onGroupIconChange(data) {
	groupIconData.value = {
		icon_emoji: null,
		icon_image_key: null,
		...data,
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.create-group-heading {
	position: relative;
}

.go-back {
	position: absolute;
	left: 0;
	top: 10px;
	width: 20px;
	height: 20px;
	cursor: pointer;
}

.create-group-wrapper {
	position: absolute;
	top: 0;
	bottom: 0;
	right: 0;
	left: 280px;
	overflow-y: auto;
	z-index: 7;
}

.create-group {
	display: block;
	width: 830px;
	margin: 0 auto;
	padding: 0 40px;
	height: calc(100vh - 188px);
}

.create-group-header {
	grid-column-start: 2;
	padding-left: 20px;
	margin-bottom: 20px;
	font-family: Avenir-Black;
	font-size: 30px;
	font-weight: normal;
	line-height: 1.2;
	text-align: center;
	color: var(--neutral-800);
}

.create-group-content {
	padding: 24px 48px;
}

.members-table {
	margin-top: 24px;
	:deep(.table-row) {
		&.table-header {
			.table-cell {
				height: 50px;
			}
		}
		&:hover {
			.remove-btn {
				display: block;
			}
		}
		.remove-btn {
			display: none;
		}
	}
}

.persona {
	@include flex;
	flex-shrink: 0;
	.name {
		margin-left: 12px;
		@include ellipsis;
		max-width: 400px;
	}
}

.group-icon-picker-container {
	padding: 16px 0;
	margin-bottom: 16px;
	@include flex-center;
	justify-content: center;
}
</style>
